import { useCallback, useMemo } from 'react';

import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';

import {
	useHasFetchedSpaces,
	useSpaceConversationIds,
	useSpaces,
	useSpacesError,
	useSpacesLoading,
	useSpaceStoreActions,
} from '../../controllers/space-store/hooks';
import type { Space } from '../../controllers/space-store/types';

type UseListSpacesProps = {
	cloudId: string;
	product: string;
	experienceId: string;
	/**
	 * When `true`, the hook skips the `fetchSpaces` network call entirely.
	 * The store state (empty arrays, no loading) is still returned so callers
	 * can safely destructure without null-checks. Useful for gating the spaces
	 * feature — pass `skip: !fg('rovo_chat_spaces_fe')` to prevent a
	 * `GET /spaces` request when the feature is disabled.
	 */
	skip?: boolean;
};

type UseListSpacesResult = {
	spaces: Space[];
	isLoading: boolean;
	error: Error | null;
	/** Flat list of all `conversationIds` across every source in every space. */
	conversationIds: string[];
	/** Refetches the list of spaces from the BE and repopulates the SpaceStore. */
	refetch: () => Promise<void>;
	hasFetched: boolean;
};

/**
 * Lists all spaces visible to the current cloud/product/experience.
 *
 * Wraps the shared `useQuery` for fetch lifecycle (mount, key-change, abort
 * on unmount) but keeps the spaces sweet-state store as the single source of
 * truth for `spaces` / `isLoading` / `error`. The store is what receives the
 * cross-hook optimistic updates (`addPendingSpace`, `removeSpace`, etc.) so
 * mutations from sibling hooks remain visible without a refetch.
 *
 * Public API is unchanged from the previous manual implementation.
 */
export const useListSpaces = ({
	cloudId,
	product,
	experienceId,
	skip = false,
}: UseListSpacesProps): UseListSpacesResult => {
	const { fetchSpaces } = useSpaceStoreActions();
	const spaces = useSpaces();
	const isLoading = useSpacesLoading();
	const error = useSpacesError();
	const hasFetched = useHasFetchedSpaces();
	const conversationIds = useSpaceConversationIds();

	// `queryFn` returns `undefined` because the canonical state lives in the
	// sweet-state store; `useQuery`'s own cache is only used to drive the
	// fetch lifecycle (mount-trigger, key-change). The store receives the
	// actual response via `setSpaces` inside `fetchSpaces`.
	//
	// We pass `force: true` so the store's own `hasFetchedList` short-circuit
	// never causes the fetch to no-op — `useQuery` already owns the
	// dedupe-by-key behaviour (it only invokes `queryFn` once per key per
	// mount), so the store's separate caching layer is redundant here and
	// can otherwise hide a needed refetch on `queryKey` change.
	const { refetch: refetchQuery } = useQuery<void>({
		queryKey: ['rovo-spaces:listSpaces', cloudId, product, experienceId],
		queryFn: () =>
			fetchSpaces({
				cloudId,
				product,
				experienceId,
				force: true,
			}),
		enabled: !skip,
	});

	const refetch = useCallback(async () => {
		await refetchQuery();
	}, [refetchQuery]);

	return useMemo(
		() => ({ spaces, isLoading, error, hasFetched, conversationIds, refetch }),
		[spaces, isLoading, error, hasFetched, conversationIds, refetch],
	);
};
